<div
  class="pricing-rules"
  [class.pricing-rules--invalid]="rules.hasError('overlappingRules')"
>
  <div class="mb-4 flex flex-wrap items-start justify-between gap-4">
    <div>
      <h3 class="text-base font-semibold text-gray-900">
        Weekly price intervals
      </h3>
      <p class="mt-1 text-sm text-secondary">
        Choose a predefined time frame or select custom hours in 30-minute
        increments. Custom end times must be later than start times; use the
        predefined Overnight interval for next-day pricing. Any uncovered time
        uses the default price.
      </p>
    </div>
    <button
      class="add-interval-button"
      mat-stroked-button
      color="primary"
      type="button"
      (click)="addRule()"
    >
      <mat-icon svgIcon="heroicons_outline:plus"></mat-icon>
      Add interval
    </button>
  </div>

  <div *ngIf="rules.length === 0" class="empty-state">
    No intervals yet. All bookings will use the default hourly price.
  </div>

  <div
    *ngFor="let rule of rules.controls; let index = index"
    class="rule-card"
    [formGroup]="rule"
  >
    <div class="mb-3 flex items-center justify-between gap-3">
      <span class="font-medium text-gray-800">Interval {{ index + 1 }}</span>
      <button
        mat-icon-button
        type="button"
        aria-label="Remove pricing interval"
        (click)="removeRule(index)"
      >
        <mat-icon svgIcon="heroicons_outline:trash"></mat-icon>
      </button>
    </div>

    <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div class="sm:col-span-2 lg:col-span-4">
        <div class="mb-2 text-sm font-medium text-default">Interval type</div>
        <mat-button-toggle-group
          class="interval-mode-toggle"
          formControlName="intervalMode"
          aria-label="Pricing interval type"
          (change)="setIntervalMode(rule, $event.value)"
        >
          <mat-button-toggle value="predefined">
            Predefined intervals
          </mat-button-toggle>
          <mat-button-toggle value="custom">Custom hours</mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      <mat-form-field
        *ngIf="isPredefined(rule)"
        class="sm:col-span-2 lg:col-span-2"
        subscriptSizing="dynamic"
      >
        <mat-label>Time frame</mat-label>
        <mat-select
          formControlName="timeBand"
          required
          (selectionChange)="selectTimeBand(rule, $event.value)"
        >
          <mat-option
            *ngFor="let timeBand of timeBands"
            [value]="timeBand.key"
            [disabled]="!isTimeBandAllowed(rule, timeBand.key)"
          >
            {{ timeBand.label }} · {{ timeBand.rangeLabel }}
          </mat-option>
        </mat-select>
        <mat-hint *ngIf="isOvernight(rule)">Ends the next day</mat-hint>
        <mat-error>Select a predefined interval.</mat-error>
      </mat-form-field>

      <mat-form-field *ngIf="!isPredefined(rule)" subscriptSizing="dynamic">
        <mat-label>Start time</mat-label>
        <mat-select formControlName="startTime" required>
          <mat-option
            *ngFor="let timeOption of customTimeOptions"
            [value]="timeOption.value"
            [disabled]="!isStartTimeAllowed(rule, timeOption.value)"
          >
            {{ timeOption.label }}
          </mat-option>
        </mat-select>
        <mat-error
          *ngIf="rule.get('startTime')?.hasError('thirtyMinuteInterval')"
        >
          Choose a time in 30-minute increments.
        </mat-error>
        <mat-error
          *ngIf="!rule.get('startTime')?.hasError('thirtyMinuteInterval')"
        >
          Enter a valid start time.
        </mat-error>
      </mat-form-field>

      <mat-form-field *ngIf="!isPredefined(rule)" subscriptSizing="dynamic">
        <mat-label>End time</mat-label>
        <mat-select formControlName="endTime" required>
          <mat-option
            *ngFor="let timeOption of customTimeOptions"
            [value]="timeOption.value"
            [disabled]="!isEndTimeAllowed(rule, timeOption.value)"
          >
            {{ timeOption.label }}
          </mat-option>
        </mat-select>
        <mat-error
          *ngIf="rule.get('endTime')?.hasError('thirtyMinuteInterval')"
        >
          Choose a time in 30-minute increments.
        </mat-error>
        <mat-error
          *ngIf="!rule.get('endTime')?.hasError('thirtyMinuteInterval')"
        >
          Enter a valid end time.
        </mat-error>
      </mat-form-field>

      <mat-form-field
        class="sm:col-span-2 lg:col-span-2"
        subscriptSizing="dynamic"
      >
        <mat-label>Price per hour</mat-label>
        <span matPrefix class="mr-2">AED</span>
        <input matInput type="number" min="0" formControlName="pricePerHour" />
        <mat-error>Enter a non-negative hourly price.</mat-error>
      </mat-form-field>

      <div class="lg:col-span-4">
        <div class="mb-2 text-sm font-medium text-default">Applies on</div>
        <div class="flex flex-wrap gap-x-3 gap-y-1">
          <mat-checkbox
            *ngFor="let day of days"
            class="day-checkbox"
            color="primary"
            [checked]="isDaySelected(rule, day.value)"
            [disabled]="!isDaySelectionAllowed(rule, day.value)"
            [attr.aria-label]="day.label"
            (change)="toggleDay(rule, day.value, $event)"
          >
            {{ day.short }}
          </mat-checkbox>
        </div>
        <div
          *ngIf="
            rule.get('daysOfWeek')?.touched && rule.get('daysOfWeek')?.invalid
          "
          class="mt-1 text-sm text-red-600"
        >
          Select at least one day.
        </div>
      </div>
    </div>
    <div
      *ngIf="rule.hasError('customTimeOrder')"
      class="mt-3 text-sm text-red-600"
      role="alert"
    >
      Custom end time must be later than start time.
    </div>
  </div>

  <div
    *ngIf="rules.hasError('zeroLengthRule')"
    class="mt-3 text-sm text-red-600"
    role="alert"
  >
    Pricing interval start and end times must be different.
  </div>

  <div
    *ngIf="rules.hasError('overlappingRules')"
    class="mt-3 text-sm text-red-600"
    role="alert"
  >
    Pricing intervals cannot overlap in the weekly schedule, including next-day
    hours.
  </div>
</div>
